// components/LoginPage.tsx
import { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { FaGoogle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useRandomText } from '../hooks/useRandomText';
import { useIsPWA } from '../hooks/useIsPWA';
import { loginSubtitles } from '../utils/phrases';

const IconGoogle = FaGoogle as any;

const LoginPage = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const randomSubtitle = useRandomText(loginSubtitles);
  
  // Detectar si es PWA
  const isPWA = useIsPWA();

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    try {
      if (isRegistering) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err: any) {
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Este correo electrónico ya está registrado.');
          break;
        case 'auth/invalid-email':
          setError('El formato del correo electrónico no es válido.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Correo o contraseña incorrectos.');
          break;
        default:
          setError('Ocurrió un error. Por favor, intenta de nuevo.');
          break;
      }
    }
  };
  
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error al iniciar sesión con Google:", error);
    }
  };

  // Función para abrir en navegador
  const openInBrowser = () => {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_blank');
  };

  return (
    <main className="bg-gradient-to-br from-background to-secondary min-h-screen w-full flex justify-center items-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        className="w-full max-w-md p-6 sm:p-8 space-y-4 sm:space-y-6 bg-white/60 backdrop-blur-xl rounded-2xl shadow-2xl"
      >
        <div className="text-center">
          <h1 className="font-script text-4xl sm:text-5xl text-primary">
            {isRegistering ? 'Crea tu Cuenta' : 'Hola linda!'}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-text-light h-10 flex items-center justify-center">
            {isRegistering ? 'Solo será necesario en esta ocasión.' : randomSubtitle}
          </p>
        </div>

        <form onSubmit={handleCredentialsSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Correo electrónico"
            required
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            required
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            className="w-full py-3 bg-primary text-white font-bold rounded-xl shadow-lg hover:bg-primary-hover transition-all duration-300 transform hover:scale-105"
          >
            {isRegistering ? 'Crear Cuenta' : 'Iniciar Sesión'}
          </button>
        </form>
        
        <div className="text-center">
          <button onClick={() => setIsRegistering(!isRegistering)} className="text-sm text-primary hover:underline">
            {isRegistering ? '¿Ya tienes una cuenta? Inicia Sesión' : '¿No tienes cuenta? Regístrate'}
          </button>
        </div>

        <div className="flex items-center">
          <hr className="flex-grow border-gray-300" />
          <span className="px-4 text-text-light">o</span>
          <hr className="flex-grow border-gray-300" />
        </div>

        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 py-3 bg-white text-text-main font-bold rounded-xl shadow-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
        >
          <IconGoogle />
          <span>Continuar con Google</span>
        </button>

        {/* ENLACE PARA ABRIR EN NAVEGADOR - Solo visible en PWA */}
        {isPWA && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center pt-2"
          >
            <button 
              onClick={openInBrowser}
              className="text-xs text-gray-500 hover:text-primary underline transition-colors duration-200"
            >
              Abrir en navegador
            </button>
          </motion.div>
        )}
      </motion.div>
    </main>
  );
};

export default LoginPage;